import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  FileDown, Receipt, Users, CalendarDays, ClipboardList, Building2,
  FileSpreadsheet, DollarSign, Award, Package, RotateCcw, LogOut,
  UserX, TrendingUp, Clock, Filter
} from "lucide-react";
import api from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const reports = [
  // Original Reports
  {
    id: "employee-directory",
    title: "Employee Directory",
    description: "Full list of active employees with contact details and departments",
    icon: Users,
    color: "bg-violet-100 text-violet-700",
    endpoint: "/reports/employee-directory",
    params: [],
    format: "pdf",
  },
  {
    id: "attendance-summary",
    title: "Attendance Summary (PDF)",
    description: "Attendance rates per employee with present/absent day counts",
    icon: CalendarDays,
    color: "bg-teal-100 text-teal-700",
    endpoint: "/reports/attendance-summary",
    params: ["start_date", "end_date"],
    format: "pdf",
  },
  {
    id: "leave-summary",
    title: "Leave Summary (PDF)",
    description: "All leave requests with type, dates, and approval status",
    icon: ClipboardList,
    color: "bg-amber-100 text-amber-700",
    endpoint: "/reports/leave-summary",
    params: [],
    format: "pdf",
  },
  // New Comprehensive Reports
  {
    id: "employee-master",
    title: "1. Employee Master Report",
    description: "Complete employee master data with all personal and professional details",
    icon: FileSpreadsheet,
    color: "bg-purple-100 text-purple-700",
    endpoint: "/reports/employee-master",
    params: [],
    format: "excel",
  },
  {
    id: "department-headcount",
    title: "2. Department Headcount",
    description: "Department-wise employee headcount with percentages",
    icon: Building2,
    color: "bg-emerald-100 text-emerald-700",
    endpoint: "/reports/department-headcount",
    params: [],
    format: "excel",
  },
  {
    id: "attendance-detailed",
    title: "3. Daily/Monthly Attendance",
    description: "Day-wise attendance matrix for all employees",
    icon: Clock,
    color: "bg-cyan-100 text-cyan-700",
    endpoint: "/reports/attendance-detailed",
    params: ["month", "year"],
    format: "excel",
  },
  {
    id: "absenteeism",
    title: "4. Absenteeism Report",
    description: "Absenteeism analysis with late days, half days, and LOP",
    icon: UserX,
    color: "bg-red-100 text-red-700",
    endpoint: "/reports/absenteeism",
    params: ["month", "year"],
    format: "excel",
  },
  {
    id: "leave-balance",
    title: "5. Leave Balance Report",
    description: "All employees' leave balances by leave type",
    icon: ClipboardList,
    color: "bg-indigo-100 text-indigo-700",
    endpoint: "/reports/leave-balance",
    params: [],
    format: "excel",
  },
  {
    id: "salary-monthly",
    title: "6. Monthly Salary Report",
    description: "Detailed salary breakdown with earnings, deductions, and net pay",
    icon: DollarSign,
    color: "bg-green-100 text-green-700",
    endpoint: "/reports/salary-monthly",
    params: ["month_name", "year"],
    format: "excel",
  },
  {
    id: "appraisals",
    title: "7. Employee Appraisal Report",
    description: "Performance appraisals with ratings and increment details",
    icon: Award,
    color: "bg-yellow-100 text-yellow-700",
    endpoint: "/reports/appraisals",
    params: ["year"],
    format: "excel",
  },
  {
    id: "asset-allocation",
    title: "8. Asset Allocation Report",
    description: "Assets currently allocated to employees",
    icon: Package,
    color: "bg-blue-100 text-blue-700",
    endpoint: "/reports/asset-allocation",
    params: [],
    format: "excel",
  },
  {
    id: "asset-returns",
    title: "9. Asset Return Report",
    description: "History of assets returned by employees",
    icon: RotateCcw,
    color: "bg-slate-100 text-slate-700",
    endpoint: "/reports/asset-returns",
    params: [],
    format: "excel",
  },
  {
    id: "fnf-settlements",
    title: "10. F&F Settlement Report",
    description: "Full & Final settlements with earnings and deductions",
    icon: Receipt,
    color: "bg-pink-100 text-pink-700",
    endpoint: "/reports/fnf-settlements",
    params: [],
    format: "excel",
  },
  {
    id: "exit-employees",
    title: "11. Exit Employee Report",
    description: "Exit/resigned employees with clearance and interview status",
    icon: LogOut,
    color: "bg-orange-100 text-orange-700",
    endpoint: "/reports/exit-employees",
    params: [],
    format: "excel",
  },
];

const Reports = () => {
  const { currentBusiness } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [employeeStatus, setEmployeeStatus] = useState("active");

  const fetchDepartments = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      const res = await api.get(`/departments?business_id=${currentBusiness.id}`);
      setDepartments(res.data || []);
    } catch (err) {
      // Silently fail - departments are optional filter
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const downloadReport = async (report) => {
    if (!currentBusiness?.id) {
      toast.error("Select a business first");
      return;
    }
    setDownloading(report.id);
    try {
      let url = `${BACKEND_URL}/api${report.endpoint}?business_id=${currentBusiness.id}`;
      
      // Add parameters based on report type
      if (report.params.includes("start_date") && startDate) url += `&start_date=${startDate}`;
      if (report.params.includes("end_date") && endDate) url += `&end_date=${endDate}`;
      if (report.params.includes("month")) url += `&month=${selectedMonth + 1}`;
      if (report.params.includes("month_name")) url += `&month=${MONTHS[selectedMonth]}`;
      if (report.params.includes("year")) url += `&year=${selectedYear}`;
      
      // Add advanced filters
      if (selectedDepartment && selectedDepartment !== "all") {
        url += `&department_id=${selectedDepartment}`;
      }
      if (employeeStatus && employeeStatus !== "all") {
        url += `&status=${employeeStatus}`;
      }

      const token = localStorage.getItem("token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      if (!res.ok) throw new Error("Failed to generate report");
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const ext = report.format === "excel" ? "xlsx" : "pdf";
      a.download = `${report.id}_${new Date().toISOString().split("T")[0]}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`${report.title} downloaded`);
    } catch {
      toast.error("Failed to generate report");
    } finally {
      setDownloading(null);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Select a business first</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">Reports</h1>
        <p className="text-slate-500">Generate and download comprehensive reports</p>
      </div>

      {/* Filters Card */}
      <Card className="card-soft">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            {/* Date Range for PDF Attendance */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Start Date</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="input-field w-[160px]" data-testid="report-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">End Date</Label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="input-field w-[160px]" data-testid="report-end-date" />
            </div>
            
            {/* Month/Year for Excel Reports */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={String(selectedMonth)} onValueChange={v => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-[140px]" data-testid="report-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Year</Label>
              <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]" data-testid="report-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEARS.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Advanced Filters */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Department</Label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-[160px]" data-testid="report-department-select">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Employee Status</Label>
              <Select value={employeeStatus} onValueChange={setEmployeeStatus}>
                <SelectTrigger className="w-[130px]" data-testid="report-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legacy PDF Reports */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">PDF Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.filter(r => r.format === "pdf").map((report) => {
            const Icon = report.icon;
            const isLoading = downloading === report.id;
            return (
              <Card key={report.id} className="card-soft hover:shadow-md transition-shadow" data-testid={`report-card-${report.id}`}>
                <CardContent className="p-5">
                  <div className={`inline-flex p-2.5 rounded-xl ${report.color} mb-3`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-1 text-sm">{report.title}</h3>
                  <p className="text-xs text-slate-500 mb-3">{report.description}</p>
                  <Button size="sm" className="w-full btn-primary" onClick={() => downloadReport(report)} disabled={isLoading}
                    data-testid={`download-report-${report.id}`}>
                    {isLoading ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    ) : (
                      <FileDown className="h-4 w-4 mr-2" />
                    )}
                    {isLoading ? "Generating..." : "Download PDF"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          {/* Payslip Card */}
          <Card className="card-soft hover:shadow-md transition-shadow" data-testid="report-card-payslip">
            <CardContent className="p-5">
              <div className="inline-flex p-2.5 rounded-xl bg-blue-100 text-blue-700 mb-3">
                <Receipt className="h-5 w-5" />
              </div>
              <h3 className="font-semibold text-slate-800 mb-1 text-sm">Payslip</h3>
              <p className="text-xs text-slate-500 mb-3">Download individual employee payslip from Payroll page</p>
              <Button size="sm" variant="outline" className="w-full rounded-full" onClick={() => window.location.href = "/payroll"}
                data-testid="go-to-payroll-btn">
                Go to Payroll
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Comprehensive Excel Reports */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 mb-3">Comprehensive Excel Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {reports.filter(r => r.format === "excel").map((report) => {
            const Icon = report.icon;
            const isLoading = downloading === report.id;
            return (
              <Card key={report.id} className="card-soft hover:shadow-md transition-shadow" data-testid={`report-card-${report.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`inline-flex p-2 rounded-lg ${report.color} shrink-0`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-800 text-sm leading-tight">{report.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{report.description}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="w-full rounded-full text-xs" onClick={() => downloadReport(report)} disabled={isLoading}
                    data-testid={`download-report-${report.id}`}>
                    {isLoading ? (
                      <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full mr-2"></div>
                    ) : (
                      <FileSpreadsheet className="h-3 w-3 mr-2" />
                    )}
                    {isLoading ? "Generating..." : "Download Excel"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Reports;
